import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Landmark, Users, GraduationCap, Compass, BarChart3, FileText, Check, ArrowRight, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const IMG = "https://images.unsplash.com/photo-1776437209011-eb19d8a05b41?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const CAPS = [
  { icon: Users, t: "Care Workforce Development", d: "Recruit and grow the direct-care workforce." },
  { icon: GraduationCap, t: "Digital Training", d: "Scalable caregiver training and onboarding." },
  { icon: Compass, t: "Care Navigation", d: "Help residents find and access care." },
  { icon: BarChart3, t: "Workforce Analytics", d: "Program dashboards and workforce insights." },
  { icon: FileText, t: "Program Reporting", d: "Reporting tools for program administration." },
  { icon: Landmark, t: "Provider Network Support", d: "Infrastructure for provider and program networks." },
];
const PROCUREMENT = [["Legal Company Name", "To Be Added / Verified"], ["UEI", "To Be Added / Verified"], ["CAGE Code", "To Be Added / Verified"], ["NAICS Codes", "To Be Added / Verified"], ["SAM.gov Status", "To Be Added / Verified"], ["Certifications", "To Be Added / Verified"], ["Service Areas", "To Be Added / Verified"]];

export default function Government() {
  const navigate = useNavigate();
  return (
    <div>
      <section className="relative bg-blue-950 text-white">
        <img src={IMG} alt="Civic building" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative">
          <p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold mb-3">T5 Government Solutions</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold max-w-2xl leading-tight">Technology for stronger care systems.</h1>
          <p className="mt-4 text-blue-100/85 max-w-2xl text-lg">Workforce, training and care-navigation technology for municipalities, counties, states, workforce boards and public-sector organizations.</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button onClick={() => navigate("/request-demo")} data-testid="gov-consult" className="h-12 px-7 bg-teal-500 hover:bg-teal-600 text-white">Request Government Consultation</Button>
            <Button onClick={() => document.getElementById("caps")?.scrollIntoView({ behavior: "smooth" })} variant="outline" className="h-12 px-7 border-white/40 text-white bg-white/5 hover:bg-white/10">Explore Capabilities</Button>
          </div>
        </div>
      </section>

      <section id="caps" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="font-display text-3xl font-bold text-blue-950 text-center mb-10">Capabilities</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CAPS.map((c, i) => (
            <motion.div key={c.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06 }}
              className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-50 text-teal-700 mb-4"><c.icon className="w-6 h-6" /></span>
              <h3 className="font-display font-semibold text-blue-950">{c.t}</h3>
              <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-blue-900/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-2 justify-center mb-3"><ShieldCheck className="w-5 h-5 text-teal-600" /><h2 className="font-display text-2xl font-bold text-blue-950">Procurement Information</h2></div>
          <p className="text-center text-sm text-slate-500 mb-8">The following will be added and verified for public-sector procurement. Placeholders shown intentionally.</p>
          <div className="rounded-2xl border border-blue-900/10 overflow-hidden">
            {PROCUREMENT.map(([k, v], i) => (
              <div key={k} className={`flex items-center justify-between px-5 py-3.5 text-sm ${i % 2 ? "bg-[#FAF9F6]" : "bg-white"}`}>
                <span className="text-slate-600 font-medium">{k}</span>
                <span className="text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full text-xs font-semibold">{v}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8"><Button onClick={() => navigate("/request-demo")} className="h-12 px-7 bg-blue-950 hover:bg-blue-900 text-white gap-2">Request Government Consultation <ArrowRight className="w-4 h-4" /></Button></div>
        </div>
      </section>
    </div>
  );
}
